import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';

interface TrendData {
  [key: string]: any;
  timestamp?: string;
  date?: string;
}

interface ChartLine {
  dataKey: string;
  name: string;
  color: string;
  yAxisId?: string;
  strokeDashArray?: string;
}

interface UnifiedTrendChartProps {
  data: TrendData[];
  lines: ChartLine[];
  type?: 'line' | 'area';
  height?: number;
  loading?: boolean;
  showLegend?: boolean;
  dualAxis?: boolean;
}

export const UnifiedTrendChart: React.FC<UnifiedTrendChartProps> = ({
  data,
  lines,
  type = 'line',
  height = 320,
  loading = false,
  showLegend = true,
  dualAxis = false
}) => {
  if (loading) {
    return (
      <div className={`h-${height} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const formatTime = (value: string) => {
    const date = new Date(value);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (value: string) => {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const Chart = type === 'area' ? AreaChart : LineChart;
  const Element = type === 'area' ? Area : Line;

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis 
            dataKey={data[0]?.timestamp ? 'timestamp' : 'date'}
            tickFormatter={data[0]?.timestamp ? formatTime : formatDate}
            className="text-xs"
          />
          <YAxis 
            yAxisId="left"
            orientation="left"
            className="text-xs"
          />
          {dualAxis && (
            <YAxis 
              yAxisId="right"
              orientation="right"
              className="text-xs"
            />
          )}
          <Tooltip 
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(229, 231, 235, 0.8)',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
          {showLegend && <Legend />}
          
          {lines.map((line, index) => (
            <Element
              key={line.dataKey}
              yAxisId={line.yAxisId || 'left'}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.color}
              strokeWidth={2}
              name={line.name}
              strokeDasharray={line.strokeDashArray}
              {...(type === 'line' ? { dot: { fill: line.color, strokeWidth: 2, r: 3 } } : { fill: `${line.color}20` })}
            />
          ))}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
};